import { useEffect, useId, useState } from "react"
import TRTC, { LocalStream } from "trtc-js-sdk"
import { TRTCConnectionInfo } from "./trtc-def"
import { usePromise } from "popopo-lib-front/lib/util"
import { Alert, Col, Row, Spinner } from "react-bootstrap"

type Props = {
  connectionInfo: TRTCConnectionInfo
}

export function TRTCCall(props: Props) {
  const remoteStreamContainerId = useId()
  const localStreamContainerId = useId()

  const trtc = useTRTC({
    connectionInfo: props.connectionInfo,
    localStreamContainerId,
    remoteStreamContainerId,
  })

  return (
    <>
      <Row>
        <Col>
          <MediaSelector localStream={trtc.localStream} kind="videoinput" />
        </Col>
        <Col>
          <MediaSelector localStream={trtc.localStream} kind="audioinput" />
        </Col>
      </Row>
      <Row>
        <Col>
          <h4>Local</h4>
          <div id={localStreamContainerId} />
        </Col>
        <Col>
          <h4>Remote</h4>
          <div id={remoteStreamContainerId} />
        </Col>
      </Row>
    </>
  )
}

type UseTRTCProps = {
  connectionInfo: TRTCConnectionInfo
  localStreamContainerId: string
  remoteStreamContainerId: string
}

export function useTRTC(props: UseTRTCProps) {
  const [localStreamState, setLocalStream] = useState<LocalStream>()

  const client = TRTC.createClient({
    mode: "rtc",
    sdkAppId: props.connectionInfo.appId,
    userId: props.connectionInfo.userId,
    userSig: props.connectionInfo.userSig,
    useStringRoomId: true,
    autoSubscribe: true,
  })

  // https://www.tencentcloud.com/jp/document/product/647/48423
  useEffect(() => {
    console.info("Initializing...", props.connectionInfo)
    client.on("stream-added", (event) => {
      console.info(`Stream Added: Id ${event.stream.getId()}`)
      const remoteStream = event.stream
      void client.subscribe(remoteStream)
    })

    client.on("stream-subscribed", (event) => {
      console.info(`Stream Subscribed: Id ${event.stream.getId()}`)
      const remoteStream = event.stream
      void remoteStream.play(props.remoteStreamContainerId)

      remoteStream.on("error", (error) => {
        console.error("Remote stream error:", error)
      })
    })

    let localStream: LocalStream

    void (async () => {
      try {
        console.info("Joining room...")
        await client.join({ roomId: props.connectionInfo.roomId, privateMapKey: props.connectionInfo.privateMapKey })
        console.info("Successfully joined room")

        localStream = TRTC.createStream({
          userId: props.connectionInfo.userId,
          audio: true,
          video: true,
        })
        await localStream.initialize()
        void localStream.play(props.localStreamContainerId)
        await client.publish(localStream)

        setLocalStream(localStream)
      } catch (error) {
        console.error("Failed to join room", error)
      }
    })()

    return () => {
      void (async () => {
        console.info("Closing...", props.connectionInfo)
        localStream?.close()
        await client.leave()
        client.destroy()
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.connectionInfo, props.localStreamContainerId, props.remoteStreamContainerId])

  return {
    client: client,
    localStream: localStreamState,
  }
}

export function MediaSelector({ localStream, kind }: { localStream?: LocalStream; kind: MediaDeviceKind }) {
  const devices = usePromise(async () => {
    const devices = await TRTC.getDevices()
    return devices.filter((device) => device.kind === kind)
  })

  if (!localStream || devices === "loading") {
    return <Spinner />
  }

  if (devices === "error") {
    return <Alert variant="danger">デバイス情報を取得できませんでした</Alert>
  }

  return (
    <select
      className="form-select"
      onChange={(event) => {
        if (event.target.value) {
          void localStream.switchDevice(kind === "videoinput" ? "video" : "audio", event.target.value)
        }
      }}
    >
      {devices.map((device) => {
        return (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label}
          </option>
        )
      })}
    </select>
  )
}
